import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Space, Table, Tag } from 'antd';
import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import { useConnection, useUser } from '../../connection/connection';
import { useBusinessContext } from '../../context/BusinessContext';
import { Expense, ExpenseStatus } from '../../dto/Expense';
import ExpenseCreateModal, {
  useExpenseCreateModal,
} from './ExpenseCreateModal';
import { WebUxTitle } from '../WebUxContext';
import { useEventListener } from '../../common/EventContext';
import ExpenseViewModal, { useExpenseViewModal } from './ExpenseViewModal';
import { Permission } from '../../dto';
import { Utils } from '../../common/Utils';

export const Expenses: FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [items, setItems] = useState<Expense[]>([]);
  const connection = useConnection();
  const business = useBusinessContext();
  const [search, setSearch] = useState<string>();
  const modal = useExpenseCreateModal();
  const viewModal = useExpenseViewModal();
  const userInfo = useUser();

  const columns: any = useMemo(
    () => [
      {
        key: 'date',
        title: 'Date',
        dataIndex: 'date',
      },
      {
        key: 'site',
        title: 'Site',
        dataIndex: 'site',
        render: (site: string, record: any) => business.sites[site].name,
      },
      {
        key: 'account',
        title: 'Account',
        dataIndex: 'account',
        render: (_: any, record: any) => (
          <span>{business.accounts[record.account]?.name}</span>
        ),
      },
      {
        key: 'type',
        title: 'Type',
        dataIndex: 'type',
        render: (type: string, record: any) =>
          business.expenseTypes[type].value,
      },
      {
        key: 'refNo',
        title: 'Ref Number',
        dataIndex: 'refNo',
      },
      {
        key: 'description',
        title: 'Description',
        dataIndex: 'description',
      },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'status',
        render: (status: string, record: any) => (
          <Tag
            color={
              status === ExpenseStatus.OPEN
                ? 'processing'
                : status === ExpenseStatus.REVIEWED
                  ? 'orange'
                  : status === ExpenseStatus.APPROVED
                    ? 'lime'
                    : 'success'
            }
          >
            {status}
          </Tag>
        ),
      },
      {
        key: 'amount',
        title: 'Amount',
        dataIndex: 'amount',
        render: (value: number, record: any) => Utils.formatPrice(value),
      },
      {
        key: 'actions',
        title: 'Actions',
        dataIndex: 'id',
        render: (_: string, record: any) => (
          <div>
            {record.status === ExpenseStatus.OPEN && (
              <Button
                disabled={
                  !userInfo?.permissions?.includes(Permission.EXPENSE_CREATE)
                }
                type="link"
                onClick={() => modal.current?.edit(record)}
              >
                <EditOutlined />
              </Button>
            )}
            <Button
              type="link"
              onClick={() => viewModal.current?.open(record)}
              disabled={
                !userInfo?.permissions?.includes(Permission.EXPENSE_VIEW)
              }
            >
              <EyeOutlined />
            </Button>
          </div>
        ),
      },
    ],
    [
      business.accounts,
      business.expenseTypes,
      business.sites,
      modal,
      userInfo?.permissions,
      viewModal,
    ],
  );
  useEffect(() => {
    setLoading(true);
    connection
      .get<Expense[]>(`expenses/${business.businessId}`)
      .then((res) =>
        setItems(
          res.data.sort((a, b) => {
            const dateComparison =
              new Date(b.date).getTime() - new Date(a.date).getTime();
            return dateComparison === 0
              ? b.createdAt - a.createdAt
              : dateComparison;
          }),
        ),
      )
      .finally(() => setLoading(false));
  }, [business.businessId, connection]);

  useEventListener(
    'EXPENSE',
    useCallback((dt) => {
      const item: Expense = dt.data;
      if (dt.action === 'CREATE') {
        setItems((prevState) => [item, ...prevState]);
      } else if (dt.action === 'UPDATE') {
        setItems((prevState) =>
          prevState.map((a) => (a.expenseId === item.expenseId ? item : a)),
        );
      }
    }, []),
  );
  return (
    <div className={'flex flex-col gap-2'}>
      <WebUxTitle>Expenses</WebUxTitle>
      <div className={'flex gap-1 justify-end'}>
        <Button
          disabled={!userInfo?.permissions?.includes(Permission.EXPENSE_CREATE)}
          type="primary"
          onClick={() => modal.current?.create()}
        >
          Create Expense
        </Button>
      </div>
      <Table
        size="small"
        loading={loading}
        pagination={false}
        columns={columns}
        dataSource={items}
      />
      <ExpenseCreateModal ref={modal} />
      <ExpenseViewModal ref={viewModal} />
    </div>
  );
};
